/**
 * Progress photos — body photos by date and pose (front / side / back / other),
 * shown as a timeline or side-by-side compare. Private: images are served only
 * to the client and their coach (GET /api/progress-photos/:id/image).
 *
 *   GET    /api/progress-photos
 *   POST   /api/progress-photos?taken_on=&pose=   (resized here to ≤ 1600px JPEG)
 *   DELETE /api/progress-photos/:id
 */
import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Camera, Trash2, X } from 'lucide-react';
import { api } from '@/lib/api';
import { Muted } from '@/components/ui';

export interface ProgressPhoto { id: string; taken_on: string; pose: string; note: string | null; url: string }

const POSES = [
  { key: 'front', label: 'Front' },
  { key: 'side', label: 'Side' },
  { key: 'back', label: 'Back' },
  { key: 'other', label: 'Other' },
];
const poseLabel = (p: string) => POSES.find((x) => x.key === p)?.label ?? 'Other';

function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function fmtDate(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Scale so the longest side is ≤ max, as JPEG (keeps the whole photo). */
async function resizeJpeg(file: File, max = 1600): Promise<Blob> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('encode_failed'))), 'image/jpeg', 0.85)
  );
}

export default function ProgressPhotos() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [pose, setPose] = useState('front');
  const [takenOn, setTakenOn] = useState(todayLocal());
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'timeline' | 'compare'>('timeline');
  const [open, setOpen] = useState<ProgressPhoto | null>(null);

  const q = useQuery({
    queryKey: ['progress-photos'],
    queryFn: () => api.get<{ photos: ProgressPhoto[] }>('/api/progress-photos'),
  });
  const photos = q.data?.photos ?? [];

  async function onPick(file: File | undefined) {
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const blob = await resizeJpeg(file);
      await api.upload(`/api/progress-photos?taken_on=${takenOn}&pose=${pose}`, blob);
      await qc.invalidateQueries({ queryKey: ['progress-photos'] });
    } catch {
      setError("Couldn't upload that photo. Try a different image.");
    } finally {
      setUploading(false);
      if (input.current) input.current.value = '';
    }
  }

  async function remove(p: ProgressPhoto) {
    if (!confirm('Delete this photo? This can’t be undone.')) return;
    await api.del(`/api/progress-photos/${p.id}`).catch(() => {});
    setOpen(null);
    qc.invalidateQueries({ queryKey: ['progress-photos'] });
  }

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-6 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--gold)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <div>
          <h1 className="display" style={{ fontSize: '1.9rem' }}>Progress Photos</h1>
          <p className="text-sm" style={{ color: 'var(--gray-400)' }}>Private — only you and your coach can see them</p>
        </div>
      </div>

      {/* Add a photo */}
      <div className="card flex flex-col gap-3">
        <div className="flex flex-wrap gap-2">
          {POSES.map((p) => (
            <button
              key={p.key}
              onClick={() => setPose(p.key)}
              className="px-3 py-1.5 text-xs font-semibold rounded-full"
              style={pose === p.key
                ? { background: 'var(--champagne)', color: 'var(--ink)' }
                : { background: 'var(--surface)', color: 'var(--cream-muted)' }}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" className="input" style={{ maxWidth: 180 }} value={takenOn} max={todayLocal()} onChange={(e) => setTakenOn(e.target.value || todayLocal())} />
          <button className="btn-gold inline-flex items-center gap-2 px-4 py-2" disabled={uploading} onClick={() => input.current?.click()}>
            <Camera size={16} /> {uploading ? 'Uploading…' : `Add ${poseLabel(pose).toLowerCase()} photo`}
          </button>
        </div>
        {error && <p className="text-xs" style={{ color: 'var(--error)' }}>{error}</p>}
        <input ref={input} type="file" accept="image/*" hidden onChange={(e) => onPick(e.target.files?.[0])} />
      </div>

      {/* Timeline / Compare */}
      <div style={{ display: 'inline-flex', borderRadius: 'var(--radius-sm)', overflow: 'hidden', border: '1px solid var(--card-border)', width: '100%' }}>
        {(['timeline', 'compare'] as const).map((m) => (
          <button
            key={m}
            onClick={() => setMode(m)}
            style={{
              flex: 1, padding: '0.5rem', fontSize: '0.82rem', fontWeight: 600, border: 'none', cursor: 'pointer',
              background: mode === m ? 'var(--gold)' : 'transparent', color: mode === m ? '#1a1a1a' : 'var(--text-tertiary)',
            }}
          >
            {m === 'timeline' ? 'Timeline' : 'Compare'}
          </button>
        ))}
      </div>

      {q.isLoading ? (
        <Muted>Loading…</Muted>
      ) : photos.length === 0 ? (
        <div className="card text-center py-8">
          <p className="font-semibold" style={{ color: 'var(--cream)' }}>No progress photos yet</p>
          <p className="text-sm mt-1" style={{ color: 'var(--cream-muted)' }}>
            Take front, side and back photos every few weeks in the same spot and lighting to see your change.
          </p>
        </div>
      ) : mode === 'timeline' ? (
        <PhotoTimeline photos={photos} onOpen={setOpen} />
      ) : (
        <PhotoCompare photos={photos} />
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.85)' }} onClick={() => setOpen(null)}>
          <div className="flex flex-col gap-3 items-center max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
            <img src={open.url} alt="" className="w-full rounded-lg object-contain" style={{ maxHeight: '75vh' }} />
            <div className="flex items-center justify-between w-full">
              <span className="text-sm" style={{ color: 'var(--cream)' }}>{fmtDate(open.taken_on)} · {poseLabel(open.pose)}</span>
              <div className="flex gap-4">
                <button onClick={() => remove(open)} className="flex items-center gap-1 text-sm" style={{ color: 'var(--error)' }}><Trash2 size={15} /> Delete</button>
                <button onClick={() => setOpen(null)} style={{ color: 'var(--cream)' }} aria-label="Close"><X size={20} /></button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Photos grouped by date, newest first. */
export function PhotoTimeline({ photos, onOpen }: { photos: ProgressPhoto[]; onOpen?: (p: ProgressPhoto) => void }) {
  const byDate = useMemo(() => {
    const m = new Map<string, ProgressPhoto[]>();
    for (const p of photos) m.set(p.taken_on, [...(m.get(p.taken_on) ?? []), p]);
    return [...m.entries()];
  }, [photos]);
  return (
    <div className="flex flex-col gap-4">
      {byDate.map(([date, list]) => (
        <div key={date}>
          <p className="text-sm font-semibold mb-2" style={{ color: 'var(--cream)' }}>{fmtDate(date)}</p>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {list.map((p) => (
              <button key={p.id} onClick={() => onOpen?.(p)} className="relative rounded-lg overflow-hidden" style={{ aspectRatio: '3 / 4', background: 'var(--surface)' }}>
                <img src={p.url} alt="" loading="lazy" className="w-full h-full object-cover" />
                <span className="absolute bottom-1 left-1 text-[10px] font-semibold px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.6)', color: 'var(--cream)' }}>
                  {poseLabel(p.pose)}
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Side by side: pick two dates and a pose (defaults: first vs latest, front). */
export function PhotoCompare({ photos }: { photos: ProgressPhoto[] }) {
  const dates = useMemo(() => [...new Set(photos.map((p) => p.taken_on))].sort(), [photos]);
  const [before, setBefore] = useState(dates[0]);
  const [after, setAfter] = useState(dates[dates.length - 1]);
  const [pose, setPose] = useState('front');
  const pick = (date: string) =>
    photos.find((p) => p.taken_on === date && p.pose === pose) ?? null;

  if (dates.length < 2) {
    return <Muted>Add photos on at least two different dates to compare.</Muted>;
  }
  const sel = (value: string, onChange: (v: string) => void) => (
    <select className="input" value={value} onChange={(e) => onChange(e.target.value)}>
      {dates.map((d) => <option key={d} value={d}>{fmtDate(d)}</option>)}
    </select>
  );
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        {POSES.map((p) => (
          <button key={p.key} onClick={() => setPose(p.key)} className="px-3 py-1.5 text-xs font-semibold rounded-full"
            style={pose === p.key ? { background: 'var(--champagne)', color: 'var(--ink)' } : { background: 'var(--surface)', color: 'var(--cream-muted)' }}>
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {[{ label: 'Before', date: before, set: setBefore }, { label: 'After', date: after, set: setAfter }].map((side) => {
          const p = pick(side.date);
          return (
            <div key={side.label} className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>{side.label}</span>
              {sel(side.date, side.set)}
              <div className="rounded-lg overflow-hidden flex items-center justify-center" style={{ aspectRatio: '3 / 4', background: 'var(--surface)' }}>
                {p ? <img src={p.url} alt="" className="w-full h-full object-cover" />
                  : <span className="text-xs px-3 text-center" style={{ color: 'var(--gray-500)' }}>No {poseLabel(pose).toLowerCase()} photo that day</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
